import { useCallback, useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  createEmployeeProfileAction,
  fetchEmployeeProfileAction,
  resetFetched,
  updateEmployeeProfileAction,
} from "@/modules/administration/features/EmployeeDirectoryManagement/slices/employeeProfileSlice";

export interface NamedOption {
  id: string;
  name: string;
}

const namedOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const createSchema = z.object({
  employee: namedOptionSchema
    .nullable()
    .refine((value) => value !== null, "Employee is required"),
  photoUuid: z.string().uuid("Photo is required"),
  role: z.string().min(1, "Role is required"),
  bio: z.string().optional().nullable(),
  email: z.string().email("A valid email is required"),
  primaryPhoneNumber: z.string().max(50).optional().nullable(),
  linkedInUrl: z.string().optional().nullable(),
  bookCallUrl: z.string().optional().nullable(),
  youtubeUrl: z.string().optional().nullable(),
  isYoutubeUrlEnabled: z.boolean().default(false),
  isPublished: z.boolean().default(false),
  specialties: z.array(namedOptionSchema).default([]),
  teams: z.array(namedOptionSchema).default([]),
});

const editSchema = createSchema.omit({ employee: true });

export type CreateEmployeeProfileFormValues = z.infer<typeof createSchema>;
export type EditEmployeeProfileFormValues = z.infer<typeof editSchema>;
// Use the create type as the form's working shape — it's a superset.
// In edit mode, `employee` is omitted from the resolver schema (so it isn't
// validated) and never read at submit time.
export type EmployeeProfileFormValues = CreateEmployeeProfileFormValues;

interface UseFormPageOptions {
  mode: "create" | "edit";
  uuid?: string;
}

export const employeeDisplayName = (employee: {
  uuid: string;
  firstName: string | null;
  lastName: string | null;
  workEmail: string | null;
}): string => {
  const full = [employee.firstName, employee.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || employee.workEmail || employee.uuid;
};

export interface UseEmployeeProfileFormPageReturn {
  form: UseFormReturn<EmployeeProfileFormValues>;
  mode: "create" | "edit";
  loadingFetch: boolean;
  submitting: boolean;

  employeeName: string | null;
  isYoutubeUrlEnabled: boolean;

  // Photo picker state (mirrors ProductStore's ProductForm pattern).
  photoDisplayUrl: string;
  photoFileName: string;
  isPhotoPickerOpen: boolean;
  openPhotoPicker: () => void;
  closePhotoPicker: () => void;
  handlePhotoSelected: (
    files: Array<{
      fileUuid: string;
      fileUrl: string;
      publicUrl: string;
      name: string;
    }>,
  ) => void;
  handleRemovePhoto: () => void;

  onSubmit: (values: EmployeeProfileFormValues) => void;
  onCancel: () => void;
}

export const useEmployeeProfileFormPage = ({
  mode,
  uuid,
}: UseFormPageOptions): UseEmployeeProfileFormPageReturn => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const fetched = useAppSelector(
    (state: RootState) => state.employeeProfile.fetched,
  );
  const loadingFetch = useAppSelector(
    (state: RootState) => state.employeeProfile.loadingFetch,
  );
  const loadingCreate = useAppSelector(
    (state: RootState) => state.employeeProfile.loadingCreate,
  );
  const loadingUpdate = useAppSelector(
    (state: RootState) => state.employeeProfile.loadingUpdate,
  );

  const schema = mode === "create" ? createSchema : editSchema;

  const defaultValues: EmployeeProfileFormValues = {
    employee: null,
    photoUuid: "",
    role: "",
    bio: "",
    email: "",
    primaryPhoneNumber: "",
    linkedInUrl: "",
    bookCallUrl: "",
    youtubeUrl: "",
    isYoutubeUrlEnabled: false,
    isPublished: false,
    specialties: [],
    teams: [],
  };

  const form = useForm<EmployeeProfileFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Edit-mode: load profile and hydrate form when fetched.
  useEffect(() => {
    if (mode === "edit" && uuid) {
      dispatch(fetchEmployeeProfileAction(uuid));
    }
    return () => {
      dispatch(resetFetched());
    };
  }, [mode, uuid, dispatch]);

  const [photoDisplayUrl, setPhotoDisplayUrl] = useState<string>("");
  const [photoFileName, setPhotoFileName] = useState<string>("");
  const [isPhotoPickerOpen, setIsPhotoPickerOpen] = useState(false);

  useEffect(() => {
    if (mode !== "edit" || !fetched) return;
    form.reset({
      photoUuid: fetched.photo.uuid,
      role: fetched.role,
      bio: fetched.bio ?? "",
      email: fetched.email,
      primaryPhoneNumber: fetched.primaryPhoneNumber ?? "",
      linkedInUrl: fetched.linkedInUrl ?? "",
      bookCallUrl: fetched.bookCallUrl ?? "",
      youtubeUrl: fetched.youtubeUrl ?? "",
      isYoutubeUrlEnabled: fetched.isYoutubeUrlEnabled,
      isPublished: fetched.isPublished,
      specialties: fetched.specialties.map((s) => ({
        id: s.uuid,
        name: s.name,
      })),
      teams: fetched.teams.map((t) => ({ id: t.uuid, name: t.name })),
    });
    setPhotoDisplayUrl(fetched.photo.publicUrl ?? "");
    setPhotoFileName("Current photo");
  }, [fetched, form, mode]);

  const openPhotoPicker = useCallback(() => setIsPhotoPickerOpen(true), []);
  const closePhotoPicker = useCallback(() => setIsPhotoPickerOpen(false), []);

  const handlePhotoSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;
      const file = files[0];
      setPhotoDisplayUrl(file.publicUrl);
      setPhotoFileName(file.name);
      form.setValue("photoUuid", file.fileUuid, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setIsPhotoPickerOpen(false);
    },
    [form],
  );

  const handleRemovePhoto = useCallback(() => {
    setPhotoDisplayUrl("");
    setPhotoFileName("");
    form.setValue("photoUuid", "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [form]);

  const isYoutubeUrlEnabled = Boolean(form.watch("isYoutubeUrlEnabled"));

  const employeeName =
    mode === "edit" && fetched ? employeeDisplayName(fetched.employee) : null;

  const goToList = useCallback(() => {
    navigate("/admin/employee-directory/profiles");
  }, [navigate]);

  const onSubmit = useCallback(
    (values: EmployeeProfileFormValues) => {
      const specialtyUuids = (values.specialties ?? []).map(
        (s: NamedOption) => s.id,
      );
      const teamUuids = (values.teams ?? []).map((t: NamedOption) => t.id);

      if (mode === "create") {
        if (!values.employee) return;
        dispatch(
          createEmployeeProfileAction(
            {
              employeeUuid: values.employee.id,
              photoUuid: values.photoUuid,
              role: values.role,
              email: values.email,
              bio: values.bio || null,
              primaryPhoneNumber: values.primaryPhoneNumber || null,
              linkedInUrl: values.linkedInUrl || null,
              bookCallUrl: values.bookCallUrl || null,
              youtubeUrl: values.isYoutubeUrlEnabled
                ? values.youtubeUrl || null
                : null,
              isYoutubeUrlEnabled: values.isYoutubeUrlEnabled,
              isPublished: values.isPublished,
              specialtyUuids,
              teamUuids,
            },
            () => {
              showNotification?.(
                "Success",
                "Employee profile created successfully.",
                "success",
              );
              goToList();
            },
          ),
        );
        return;
      }

      if (!uuid) return;
      dispatch(
        updateEmployeeProfileAction(
          uuid,
          {
            photoUuid: values.photoUuid,
            role: values.role,
            email: values.email,
            bio: values.bio || null,
            primaryPhoneNumber: values.primaryPhoneNumber || null,
            linkedInUrl: values.linkedInUrl || null,
            bookCallUrl: values.bookCallUrl || null,
            youtubeUrl: values.isYoutubeUrlEnabled
              ? values.youtubeUrl || null
              : null,
            isYoutubeUrlEnabled: values.isYoutubeUrlEnabled,
            isPublished: values.isPublished,
            specialtyUuids,
            teamUuids,
          },
          () => {
            showNotification?.(
              "Success",
              "Employee profile updated successfully.",
              "success",
            );
            goToList();
          },
        ),
      );
    },
    [dispatch, mode, uuid, goToList, showNotification],
  );

  return {
    form,
    mode,
    loadingFetch,
    submitting: mode === "create" ? loadingCreate : loadingUpdate,

    employeeName,
    isYoutubeUrlEnabled,

    photoDisplayUrl,
    photoFileName,
    isPhotoPickerOpen,
    openPhotoPicker,
    closePhotoPicker,
    handlePhotoSelected,
    handleRemovePhoto,

    onSubmit,
    onCancel: goToList,
  };
};
